import React, { useEffect, useRef, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { IconButton, Tooltip, Snackbar, Alert, CircularProgress, Badge, Box } from '@mui/material';
import { CloudDoneOutlinedIcon as CloudDoneOutlined, CloudOffOutlinedIcon as CloudOffOutlined } from "../../icons";
import { useRootStore } from '../../store/contexts';
import { ActivityQueuePanel } from './ActivityQueuePanel';

const SHOW_SSE_SNACKBAR = process.env.REACT_APP_SHOW_SSE_SNACKBAR === 'true';

export const ConnectionStatusIndicator: React.FC = observer(() => {
  const rootStore = useRootStore();

  const connectionState = rootStore.eventSourceStore.connectionState;
  const reconnectAttempts = rootStore.eventSourceStore.reconnectAttempts;
  const sseConnected = rootStore.langStore.sseConnected;
  const sseConnectionFailed = rootStore.langStore.sseConnectionFailed;
  const sseReconnecting = rootStore.langStore.sseReconnecting;
  const sseDisconnected = rootStore.langStore.sseDisconnected;

  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<'info' | 'warning' | 'error'>('info');

  const previousStatus = useRef(connectionState);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const activeCount = rootStore.activityStore.activeCount;
  const hasOperations = activeCount > 0;
  const showSpinner = activeCount === 1;
  const showBadge = activeCount > 1;

  useEffect(() => {
    if (!SHOW_SSE_SNACKBAR) return;

    const prevStatus = previousStatus.current;

    if (prevStatus !== connectionState) {
      let message = '';
      let severity: 'info' | 'warning' | 'error' = 'info';

      switch (connectionState) {
        case 'connected':
          if (prevStatus === 'error' || prevStatus === 'connecting') {
            message = sseConnected;
            severity = 'info';
            setSnackbarMessage(message);
            setSnackbarSeverity(severity);
            setShowSnackbar(true);
          }
          break;

        case 'error':
          message = sseConnectionFailed;
          severity = 'error';
          setSnackbarMessage(message);
          setSnackbarSeverity(severity);
          setShowSnackbar(true);
          break;

        case 'connecting':
          if (reconnectAttempts > 0) {
            message = sseReconnecting.replace('{attempt}', String(reconnectAttempts));
            severity = 'warning';
            setSnackbarMessage(message);
            setSnackbarSeverity(severity);
            setShowSnackbar(true);
          }
          break;
      }

      previousStatus.current = connectionState;
    }
  }, [connectionState, reconnectAttempts, sseConnected, sseConnectionFailed, sseReconnecting]);

  const activityPreview = useMemo(() => {
    if (!hasOperations) return '';
    const operations = rootStore.activityStore.activeOperations
      .map(op => op.operation)
      .join(', ');
    return operations;
  }, [hasOperations, rootStore.activityStore.activeOperations]);

  const tooltipText = useMemo(() => {
    if (hasOperations) {
      return activityPreview;
    }
    switch (connectionState) {
      case 'connected':
        return sseConnected;
      case 'connecting':
        return sseReconnecting;
      case 'error':
      case 'disconnected':
      default:
        return sseDisconnected;
    }
  }, [hasOperations, activityPreview, connectionState, sseConnected, sseReconnecting, sseDisconnected]);

  const handleClick = () => {
    if (iconButtonRef.current) {
      rootStore.activityQueueStore.togglePanel(iconButtonRef.current);
    }
  };

  const renderIcon = useMemo(() => {
    let connectionIcon;
    switch (connectionState) {
      case 'connected':
        connectionIcon = <CloudDoneOutlined sx={{ color: 'text.secondary' }} />;
        break;
      case 'connecting':
        connectionIcon = <CircularProgress size={20} color="inherit" />;
        break;
      case 'error':
      case 'disconnected':
      default:
        connectionIcon = <CloudOffOutlined color="error" />;
        break;
    }

    if (showSpinner) {
      return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {connectionIcon}
          <CircularProgress
            size={20}
            sx={{
              position: 'absolute',
              top: 2,
              left: 2,
            }}
          />
        </Box>
      );
    } else if (showBadge) {
      return (
        <Badge
          badgeContent={activeCount}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.625rem',
              minWidth: '16px',
              height: '16px',
              padding: '0 4px',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 }
              }
            }
          }}
        >
          {connectionIcon}
        </Badge>
      );
    } else {
      return connectionIcon;
    }
  }, [connectionState, showSpinner, showBadge, activeCount]);

  return (
    <>
      <Tooltip title={tooltipText} placement="bottom">
        <IconButton
          ref={iconButtonRef}
          size="large"
          onClick={handleClick}
          sx={{ cursor: hasOperations ? 'pointer' : 'default' }}
        >
          {renderIcon}
        </IconButton>
      </Tooltip>
      <ActivityQueuePanel />

      {SHOW_SSE_SNACKBAR && (
        <Snackbar
          open={showSnackbar}
          autoHideDuration={4000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
});
